import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { MedicalServiceService } from '../../service/MedicalServiceService';

const medicalServiceService = new MedicalServiceService();

const initialState = {
  medicalServiceList: [],
  medicalServiceListCount: 0,
  status: 'idle',
  error: null
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const getMedicalServicesAsync = createAsyncThunk(
  'medicalServiceList',
  async ({page = 0, first = 0, rows = 10, sortField = 'id', sortOrder = 1, filters = {}}, { rejectWithValue }) => {
    try {
      return await medicalServiceService.getMedicalServices(page, first, rows, sortField, sortOrder, filters);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar serviços médicos');
    }
  }
);

export const getMedicalServicesCountAsync = createAsyncThunk(
  'medicalServiceListCount',
  async ({filters = {}}, { rejectWithValue }) => {
    try {
      return await medicalServiceService.getCountMedicalServices(filters);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao buscar contagem de serviços médicos');
    }
  }
);

export const deleteMedicalServiceAsync = createAsyncThunk(
  'deleteMedicalService',
  async ({id, gridParameters}, { rejectWithValue }) => {
    try {
      await medicalServiceService.deleteMedicalService(id);
      const count = await medicalServiceService.getCountMedicalServices(gridParameters.filters);
      const list = await medicalServiceService.getMedicalServices(0, 0, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters);
      return {
        count,
        list
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao excluir serviço médico');
    }
  }
);

export const editMedicalServiceAsync = createAsyncThunk(
  'editMedicalService',
  async ({medicalService, gridParameters}, { rejectWithValue }) => {
    try {
      await medicalServiceService.editMedicalService(medicalService);
      const count = await medicalServiceService.getCountMedicalServices(gridParameters.filters);
      const list = await medicalServiceService.getMedicalServices(gridParameters.page, gridParameters.first, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters);
      return {
        count,
        list
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao editar serviço médico');
    }
  }
);

export const createMedicalServiceAsync = createAsyncThunk(
  'createMedicalService',
  async ({medicalService, gridParameters}, { rejectWithValue }) => {
    try {
      await medicalServiceService.createMedicalService(medicalService);
      const count = await medicalServiceService.getCountMedicalServices(gridParameters.filters);
      const list = await medicalServiceService.getMedicalServices(0, 0, gridParameters.rows, gridParameters.sortField, gridParameters.sortOrder, gridParameters.filters);
      return {
        count,
        list
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erro ao criar serviço médico');
    }
  }
);

export const medicalServiceSlice = createSlice({
  name: 'medicalServiceSlice',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(getMedicalServicesAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getMedicalServicesCountAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getMedicalServicesAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.medicalServiceList = action.payload.data;
        state.error = null;
      })
      .addCase(getMedicalServicesAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(getMedicalServicesCountAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.medicalServiceListCount = action.payload;
        state.error = null;
      })
      .addCase(getMedicalServicesCountAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(createMedicalServiceAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createMedicalServiceAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.medicalServiceList = action.payload.list.data;
        state.medicalServiceListCount = action.payload.count;
        state.error = null;
      })
      .addCase(createMedicalServiceAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(editMedicalServiceAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(editMedicalServiceAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.medicalServiceList = action.payload.list.data;
        state.medicalServiceListCount = action.payload.count;
        state.error = null;
      })
      .addCase(editMedicalServiceAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(deleteMedicalServiceAsync.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteMedicalServiceAsync.fulfilled, (state, action) => {
        state.status = 'idle';
        state.medicalServiceList = action.payload.list.data;
        state.medicalServiceListCount = action.payload.count;
        state.error = null;
      })
      .addCase(deleteMedicalServiceAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const selectMedicalServiceList = (state) => state.medicalService.medicalServiceList;

export const selectMedicalServiceListCount = (state) => state.medicalService.medicalServiceListCount;

export const selectLoading = (state) => state.medicalService.status === 'loading';

export const selectError = (state) => state.medicalService.error;

export default medicalServiceSlice.reducer;